import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { LoadingCount } from "@/core/components/ui/loading-count/loading-count";
import { cn } from "@/core/utils/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-lg p-1 text-tertiary-text",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    count?: number;
    isCountLoading?: boolean;
    size?: "small" | "medium";
  }
>(({ className, count, isCountLoading, size, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "group inline-flex items-center justify-center whitespace-nowrap rounded-md pb-0 pt-2 font-medium leading-tight ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-primary-text",
      {
        "px-6 text-lg": size === "small",
        "px-4 text-md": size !== "small",
      },
      className,
    )}
    {...props}
  >
    {props.children}
    <LoadingCount
      count={count}
      isCountLoading={isCountLoading}
      className="ml-2 text-tertiary-text group-data-[state=active]:text-secondary-text"
      size="small"
    />
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

interface TabsContentProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  allowOverflow?: boolean;
}

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, allowOverflow = false, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "min-h-0 flex-1 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      !allowOverflow && "overflow-y-hidden",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

const TabsListWithIndicator = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    containerClassName?: string;
    size?: "small" | "medium";
    hideBottomBorder?: boolean;
  }
>(
  (
    {
      className,
      children,
      containerClassName,
      size = "medium",
      hideBottomBorder = false,
      ...props
    },
    ref,
  ) => {
    const [indicator, setIndicator] = useState({ left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const activeTab = containerRef.current?.querySelector(
        '[data-state="active"]',
      );
      if (activeTab instanceof HTMLElement) {
        // Handle edge cases where offsetLeft/offsetWidth might be 0 due to hidden tab triggers
        setIndicator({
          left: activeTab.offsetLeft
            ? activeTab.offsetLeft - 4
            : indicator.left + indicator.width * 0.5,
          width: activeTab.offsetWidth ? activeTab.offsetWidth + 4 : -2,
        });
      }
    }, [children]);

    const isSmall = size === "small";

    return (
      <div
        ref={containerRef}
        className={cn(
          "relative h-fit",
          hideBottomBorder ? "" : "border-b border-stroke-tertiary",
          containerClassName,
        )}
      >
        <TabsPrimitive.List
          ref={ref}
          className={cn(
            "inline-flex items-stretch justify-center p-1 leading-6 text-tertiary-text",
            isSmall ? "h-12" : "h-16",
            className,
          )}
          {...props}
        >
          {children}
        </TabsPrimitive.List>
        <div
          className="absolute bottom-0 h-[2px] bg-stroke-tabSelected transition-all duration-300"
          style={{
            left: indicator.left,
            width: indicator.width + 2,
            transform: `translateY(1px)`,
          }}
        />
      </div>
    );
  },
);
TabsListWithIndicator.displayName = "TabsListWithIndicator";

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsListWithIndicator };
